import React, { useCallback, useEffect, useMemo, useState } from "react";
import useMediaQuery from "@mui/material/useMediaQuery";

import axios from "@helpers/axios/private.axios";
import { useTranslation } from "@helpers/translate";
import NoDateIcon from "@assets/images/icons/no-date-icon.svg";
import dayjs from "dayjs";

import AppPagination from "@components/common/AppPagination";
import { getWalletTxCalendarDayKey } from "@helpers/walletTxFormat";
import { useTablePagination } from "@components/common/hooks/useTablePagination";
import WalletTxDesktopTable from "./WalletTxDesktopTable/WalletTxDesktopTable";
import WalletTxMobileCards from "./WalletTxMobileCards/WalletTxMobileCards";
import TransactionDateRangePicker from "./TransactionDateRangePicker";

import "./Crypto.sass";

const WALLET_TX_ITEMS_PER_PAGE = 10;
const WALLET_TX_DESKTOP_MIN = 999;

/** Тимчасові фейкові дані, оскільки з бекенду повертається пустий масив */
const USE_WALLET_TX_MOCK = true;

const MOCK_WALLET_TX_ROWS = [
  {
    id: 1008421,
    created_at: "2020-01-15 10:15:00",
    amount: "250.00",
    currency: "USDT",
    gateway_title: "TRC20",
    operation: "deposit",
    status: "success",
    bonus_amount: "10.00 USDT",
  },
  {
    id: 1008422,
    created_at: "2020-09-03 14:22:00",
    amount: "500.00",
    currency: "USDT",
    gateway_title: "ERC20",
    operation: "withdraw",
    status: "pending",
    bonus_amount: null,
  },
  {
    id: 1008423,
    created_at: "2021-04-18 09:05:00",
    amount: "75.50",
    currency: "BTC",
    gateway_title: "Internal transfer",
    operation: "deposit",
    status: "unverified",
    bonus_amount: "—",
  },
  {
    id: 1008424,
    created_at: "2021-11-27 12:45:00",
    amount: "1200.00",
    currency: "ETH",
    gateway_title: "ERC20",
    operation: "deposit",
    status: "success",
    bonus_amount: "25.00 ETH",
  },
  {
    id: 1008425,
    created_at: "2022-02-14 16:30:00",
    amount: "320.75",
    currency: "USDT",
    gateway_title: "BEP20",
    operation: "withdraw",
    status: "failed",
    bonus_amount: null,
  },
  {
    id: 1008426,
    created_at: "2022-08-09 08:10:00",
    amount: "50.00",
    currency: "BTC",
    gateway_title: "Internal transfer",
    operation: "deposit",
    status: "success",
    bonus_amount: "2.00 BTC",
  },
  {
    id: 1008427,
    created_at: "2023-01-22 11:55:00",
    amount: "890.00",
    currency: "USDT",
    gateway_title: "TRC20",
    operation: "withdraw",
    status: "pending",
    bonus_amount: null,
  },
  {
    id: 1008428,
    created_at: "2023-03-30 19:40:00",
    amount: "150.25",
    currency: "LTC",
    gateway_title: "LTC Network",
    operation: "deposit",
    status: "success",
    bonus_amount: "5.00 LTC",
  },
  {
    id: 1008429,
    created_at: "2023-06-12 07:25:00",
    amount: "60.00",
    currency: "ETH",
    gateway_title: "ERC20",
    operation: "withdraw",
    status: "success",
    bonus_amount: null,
  },
  {
    id: 1008430,
    created_at: "2023-09-19 13:15:00",
    amount: "980.00",
    currency: "USDT",
    gateway_title: "BEP20",
    operation: "deposit",
    status: "success",
    bonus_amount: "15.00 USDT",
  },
  {
    id: 1008431,
    created_at: "2024-01-05 15:05:00",
    amount: "430.00",
    currency: "BTC",
    gateway_title: "Internal transfer",
    operation: "withdraw",
    status: "unverified",
    bonus_amount: null,
  },
  {
    id: 1008432,
    created_at: "2024-02-28 09:35:00",
    amount: "210.00",
    currency: "DOGE",
    gateway_title: "DOGE Network",
    operation: "deposit",
    status: "success",
    bonus_amount: "8.00 DOGE",
  },
  {
    id: 1008433,
    created_at: "2024-04-17 18:20:00",
    amount: "775.50",
    currency: "USDT",
    gateway_title: "TRC20",
    operation: "withdraw",
    status: "failed",
    bonus_amount: null,
  },
  {
    id: 1008434,
    created_at: "2024-06-25 10:50:00",
    amount: "95.00",
    currency: "ETH",
    gateway_title: "ERC20",
    operation: "deposit",
    status: "pending",
    bonus_amount: "3.00 ETH",
  },
  {
    id: 1008435,
    created_at: "2024-09-14 21:10:00",
    amount: "660.00",
    currency: "USDT",
    gateway_title: "BEP20",
    operation: "withdraw",
    status: "success",
    bonus_amount: null,
  },
  {
    id: 1008436,
    created_at: "2025-01-08 06:45:00",
    amount: "305.75",
    currency: "BTC",
    gateway_title: "Internal transfer",
    operation: "deposit",
    status: "success",
    bonus_amount: "12.00 BTC",
  },
  {
    id: 1008437,
    created_at: "2025-03-11 17:55:00",
    amount: "140.00",
    currency: "LTC",
    gateway_title: "LTC Network",
    operation: "withdraw",
    status: "pending",
    bonus_amount: null,
  },
  {
    id: 1008438,
    created_at: "2025-05-20 12:00:00",
    amount: "850.00",
    currency: "USDT",
    gateway_title: "TRC20",
    operation: "deposit",
    status: "success",
    bonus_amount: "20.00 USDT",
  },
  {
    id: 1008439,
    created_at: "2025-09-01 14:40:00",
    amount: "47.30",
    currency: "DOGE",
    gateway_title: "DOGE Network",
    operation: "withdraw",
    status: "failed",
    bonus_amount: null,
  },
  {
    id: 1008440,
    created_at: "2026-05-12 09:20:00",
    amount: "1250.00",
    currency: "ETH",
    gateway_title: "ERC20",
    operation: "deposit",
    status: "success",
    bonus_amount: "30.00 ETH",
  },
];

const FILTERS = {
  ALL: "all",
  DEPOSIT: "deposit",
  WITHDRAW: "withdraw",
};

const createEmptyDateRange = () => ({
  fromDate: null,
  toDate: null,
});

const Crypto = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(
    USE_WALLET_TX_MOCK ? MOCK_WALLET_TX_ROWS : [],
  );
  const [filter, setFilter] = useState(FILTERS.DEPOSIT);
  const [dateRange, setDateRange] = useState(createEmptyDateRange);
  const { __ } = useTranslation();
  const isMobileView = useMediaQuery(`(max-width: ${WALLET_TX_DESKTOP_MIN}px)`);

  useEffect(() => {
    setDateRange(createEmptyDateRange());
  }, [filter]);

  const getFiatHistory = useCallback(async () => {
    if (USE_WALLET_TX_MOCK) {
      setData(MOCK_WALLET_TX_ROWS);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const operationParam = filter !== "all" ? `operation=${filter}` : "";
      const dateParams =
        dateRange.fromDate && dateRange.toDate
          ? `&date_from=${dateRange.fromDate.format(
              "YYYY-MM-DD",
            )}&date_to=${dateRange.toDate.format("YYYY-MM-DD")}`
          : "";

      const resp = await axios.get(
        `/gateway/history/crypto/transactions?${operationParam}&page=1&paginate=10${dateParams}`,
      );

      setData(resp.data.data);
      setLoading(false);
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  }, [filter, dateRange?.fromDate, dateRange?.toDate]);

  useEffect(() => {
    getFiatHistory();
  }, [getFiatHistory]);

  const handleApplyDateRange = useCallback(({ fromDate, toDate }) => {
    setDateRange({
      fromDate: dayjs(fromDate).startOf("day"),
      toDate: dayjs(toDate).startOf("day"),
    });
  }, []);

  const applyDateRangeFilter = useCallback(
    (rows) => {
      const { fromDate, toDate } = dateRange;
      if (!fromDate || !toDate) return rows;

      const fromKey = dayjs(fromDate).format("YYYY-MM-DD");
      const toKey = dayjs(toDate).format("YYYY-MM-DD");
      if (!dayjs(fromDate).isValid() || !dayjs(toDate).isValid()) return rows;

      const rangeStart = fromKey <= toKey ? fromKey : toKey;
      const rangeEnd = fromKey <= toKey ? toKey : fromKey;

      return rows.filter((row) => {
        const rowDayKey = getWalletTxCalendarDayKey(row.created_at);
        if (!rowDayKey) return false;
        return rowDayKey >= rangeStart && rowDayKey <= rangeEnd;
      });
    },
    [dateRange.fromDate, dateRange.toDate],
  );

  const allTypeRows = useMemo(
    () => applyDateRangeFilter(data),
    [applyDateRangeFilter, data],
  );

  const depositRows = useMemo(
    () =>
      applyDateRangeFilter(
        data.filter((item) => item.operation === FILTERS.DEPOSIT),
      ),
    [applyDateRangeFilter, data],
  );

  const withdrawRows = useMemo(
    () =>
      applyDateRangeFilter(
        data.filter((item) => item.operation === FILTERS.WITHDRAW),
      ),
    [applyDateRangeFilter, data],
  );

  const allTypesPagination = useTablePagination({
    data: allTypeRows,
    itemsPerPage: WALLET_TX_ITEMS_PER_PAGE,
  });

  const depositPagination = useTablePagination({
    data: depositRows,
    itemsPerPage: WALLET_TX_ITEMS_PER_PAGE,
  });

  const withdrawPagination = useTablePagination({
    data: withdrawRows,
    itemsPerPage: WALLET_TX_ITEMS_PER_PAGE,
  });

  useEffect(() => {
    allTypesPagination.resetPage();
    depositPagination.resetPage();
    withdrawPagination.resetPage();
  }, [
    dateRange.fromDate,
    dateRange.toDate,
    allTypesPagination.resetPage,
    depositPagination.resetPage,
    withdrawPagination.resetPage,
  ]);

  const activeTabRows =
    filter === FILTERS.ALL
      ? allTypeRows
      : filter === FILTERS.DEPOSIT
      ? depositRows
      : withdrawRows;

  const {
    paginatedData,
    showPagination,
    paginationRange,
    currentPage,
    totalPages,
    handlePageChange,
    handleEllipsisClick,
  } =
    filter === FILTERS.ALL
      ? allTypesPagination
      : filter === FILTERS.DEPOSIT
      ? depositPagination
      : withdrawPagination;

  const hasNoResults = !loading && activeTabRows.length === 0;

  return (
    <div className="wallet-tx-table">
      <div className="wallet-tx-table__controls">
        <div className="wallet-tx-table__filters">
          <button
            className={`wallet-tx-table__button ${
              filter === FILTERS.DEPOSIT ? "is-active" : ""
            }`}
            onClick={() => setFilter(FILTERS.DEPOSIT)}
          >
            {__("wallet.deposit_history")}
          </button>

          <button
            className={`wallet-tx-table__button ${
              filter === FILTERS.WITHDRAW ? "is-active" : ""
            }`}
            onClick={() => setFilter(FILTERS.WITHDRAW)}
          >
            {__("wallet.withdrawal_history")}
          </button>

          <button
            className={`wallet-tx-table__button ${
              filter === FILTERS.ALL ? "is-active" : ""
            }`}
            onClick={() => setFilter(FILTERS.ALL)}
          >
            {__("wallet.all_types_history")}
          </button>
        </div>

        <div className="wallet-tx-table__calendar">
          <TransactionDateRangePicker
            key={filter}
            range={dateRange}
            onApply={handleApplyDateRange}
          />
        </div>
      </div>

      <div className="wallet-tx-table__scroll">
        {hasNoResults ? (
          <div className="wallet-tx-table__empty table__not-found">
            <img src={NoDateIcon} alt="" />
            <p>{__("common.no_data")}</p>
          </div>
        ) : isMobileView ? (
          <WalletTxMobileCards data={paginatedData} loading={loading} />
        ) : (
          <WalletTxDesktopTable data={paginatedData} loading={loading} />
        )}
      </div>
      {showPagination && !hasNoResults && (
        <div className="wallet-tx-table__pagination app-pagination-container">
          <AppPagination
            paginationRange={paginationRange}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onEllipsisClick={handleEllipsisClick}
          />
        </div>
      )}
    </div>
  );
};

export default Crypto;
